import { type Season, formatDate, formatTime } from '@/time/clock';
import { type Weather, WEATHER_NAMES } from '@/time/weather';

/**
 * Clock, date, weather and place.
 *
 * DOM rather than drawn into the canvas: crisp text at any device resolution,
 * readable by a screen reader, and no font baking. It only touches the DOM when
 * a displayed value actually changes — writing textContent every frame is a
 * layout thrash the profiler notices long before the player does.
 */

export interface HudState {
  readonly hour: number;
  readonly minute: number;
  readonly season: Season;
  readonly dayOfSeason: number;
  readonly weather: Weather;
  readonly areaName: string;
  /** Named when today is a festival, so the date reads as an occasion. */
  readonly festival?: string | null;
}

const WEATHER_ICONS: Readonly<Record<Weather, string>> = {
  clear: '☀',
  cloudy: '☁',
  rain: '☂',
  storm: '⚡',
  snow: '❄',
};

export class Hud {
  private readonly root: HTMLDivElement;
  private readonly timeEl: HTMLSpanElement;
  private readonly dateEl: HTMLSpanElement;
  private readonly weatherEl: HTMLSpanElement;
  private readonly areaEl: HTMLDivElement;

  private lastTime = '';
  private lastDate = '';
  private lastWeather = '';
  private lastArea = '';

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'hud';

    const panel = document.createElement('div');
    panel.className = 'hud-clock';

    this.timeEl = document.createElement('span');
    this.timeEl.className = 'hud-time';
    this.dateEl = document.createElement('span');
    this.dateEl.className = 'hud-date';
    this.weatherEl = document.createElement('span');
    this.weatherEl.className = 'hud-weather';

    panel.append(this.timeEl, this.dateEl, this.weatherEl);

    this.areaEl = document.createElement('div');
    this.areaEl.className = 'hud-area';

    this.root.append(panel, this.areaEl);
    container.appendChild(this.root);
  }

  update(state: HudState): void {
    const time = formatTime(state.hour, state.minute);
    if (time !== this.lastTime) {
      this.timeEl.textContent = time;
      this.lastTime = time;
    }

    // The festival replaces the date: on the one day a season it matters, it
    // is the more useful of the two things that line could say.
    const date = state.festival ?? formatDate(state.season, state.dayOfSeason);
    if (date !== this.lastDate) {
      this.dateEl.textContent = date;
      this.lastDate = date;
    }

    const weather = `${WEATHER_ICONS[state.weather]} ${WEATHER_NAMES[state.weather]}`;
    if (weather !== this.lastWeather) {
      this.weatherEl.textContent = weather;
      this.lastWeather = weather;
    }

    if (state.areaName !== this.lastArea) {
      this.areaEl.textContent = state.areaName;
      // Restart the fade by forcing a reflow between class removals.
      this.areaEl.classList.remove('is-showing');
      void this.areaEl.offsetWidth;
      this.areaEl.classList.add('is-showing');
      this.lastArea = state.areaName;
    }
  }

  destroy(): void {
    this.root.remove();
  }
}
